import {Textarea} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Grid} from 'src/design/atoms/Grid';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import React from 'react';
import {SectionCard} from 'src/design/atoms/SectionCard';
import type {IFormDataV2} from 'src/types/FormV2';

interface LabelDefinition {
  level: number;
  fieldName: string;
  defaultHint: string;
}

const PROBABILITY_LABELS: LabelDefinition[] = [
  {level: 1, fieldName: 'probability_1', defaultHint: 'Very Unlikely'},
  {level: 2, fieldName: 'probability_2', defaultHint: 'Slight'},
  {level: 3, fieldName: 'probability_3', defaultHint: 'Possible'},
  {level: 4, fieldName: 'probability_4', defaultHint: 'Likely'},
  {level: 5, fieldName: 'probability_5', defaultHint: 'Very Likely'},
];

const SEVERITY_LABELS: LabelDefinition[] = [
  {level: 1, fieldName: 'severity_1', defaultHint: 'Near Miss'},
  {level: 2, fieldName: 'severity_2', defaultHint: 'First Aid / Minor Damage'},
  {
    level: 3,
    fieldName: 'severity_3',
    defaultHint: 'Recordable / Partial Loss',
  },
  {
    level: 4,
    fieldName: 'severity_4',
    defaultHint: 'Lost Time / Interrupted Work',
  },
  {level: 5, fieldName: 'severity_5', defaultHint: 'Fatality / Total Loss'},
];

export interface LabelsSectionProps {
  formData: IFormDataV2;
  onChange: (name: string, value: any) => void;
  mode: 'create' | 'edit' | 'view';
  type: 'probability' | 'severity';
}

const ViewLabel: React.FC<{level: number; value: string}> = ({
  level,
  value,
}) => (
  <Grid.Col span={12}>
    <Box
      data-testid={`view-label-${level}`}
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 'var(--mantine-spacing-sm)',
      }}>
      <Text
        fw={700}
        size="sm"
        c="dimmed"
        style={{minWidth: '60px', flexShrink: 0}}>
        Level {level}
      </Text>
      <Text size="sm">{value || '—'}</Text>
    </Box>
  </Grid.Col>
);

export const LabelsSection: React.FC<LabelsSectionProps> = ({
  formData,
  onChange,
  mode,
  type,
}) => {
  const isViewMode = mode === 'view';
  const labels = type === 'probability' ? PROBABILITY_LABELS : SEVERITY_LABELS;
  const title = type === 'probability' ? 'Probability Scale' : 'Severity Scale';
  const description =
    type === 'probability'
      ? '1 = least likely, 5 = most likely.'
      : '1 = least severe, 5 = most severe.';

  if (isViewMode) {
    return (
      <SectionCard title={title} data-testid={`section-${type}-labels`}>
        <Box p="md">
          <Grid gutter="xs">
            {labels.map(label => (
              <ViewLabel
                key={label.fieldName}
                level={label.level}
                value={formData[label.fieldName]}
              />
            ))}
          </Grid>
        </Box>
      </SectionCard>
    );
  }

  return (
    <SectionCard title={title} data-testid={`section-${type}-labels`}>
      <Box p="md">
        <Text size="sm" c="dimmed" mb="sm">
          {description}
        </Text>
        <Stack gap="sm">
          {labels.map(label => (
            <Textarea
              key={label.fieldName}
              data-testid={`form-field-${label.fieldName}`}
              label={`Level ${label.level}`}
              value={formData[label.fieldName] || ''}
              onChange={e => onChange(label.fieldName, e.currentTarget.value)}
              placeholder={label.defaultHint}
              autoComplete="new-password"
              autosize
              minRows={1}
              maxRows={3}
            />
          ))}
        </Stack>
      </Box>
    </SectionCard>
  );
};
